import { channelUrl } from './channels';
import { ChannelName, WsConnection, WsListener, WsReconnectHandler } from './websocket.types';

const connections = new Map<ChannelName, WsConnection>();

// Application close codes, both real close frames. A failed upgrade is not one of these —
// the browser reports that as 1006, which stays on the retry path.
export const WS_CLOSE_UNAUTHORIZED = 4401;
export const WS_CLOSE_UNKNOWN_ROUTE = 4404;

const BASE_DELAY_MS = 1_000;
const MAX_DELAY_MS = 30_000;

// A socket must stay up this long before its success resets the backoff. Without it, a
// server that accepts and immediately drops holds the delay in the first window forever.
const STABLE_CONNECTION_MS = 10_000;

const ESCALATE_AFTER_ATTEMPTS = 5;

// Survives teardown, so a remount cannot re-dial a channel the server already refused.
const terminal = new Set<ChannelName>();

let onAuthFailure: (() => void) | null = null;

// One notification per auth event, not one per socket: every channel expires at the same
// instant, and the handler logs out.
let authFailureNotified = false;

// A 4401 landing while the single handler slot is briefly empty, during a route
// transition. Held, or the user keeps a dead page with no redirect.
let authFailurePending = false;

export const setAuthFailureHandler = (handler: (() => void) | null): void => {
  onAuthFailure = handler;
  if (handler && authFailurePending) {
    authFailurePending = false;
    authFailureNotified = true;
    handler();
  }
};

const notifyAuthFailure = (): void => {
  if (authFailureNotified) return;
  if (!onAuthFailure) {
    authFailurePending = true;
    return;
  }
  authFailureNotified = true;
  onAuthFailure();
};

const ensure = (channel: ChannelName): WsConnection => {
  const existing = connections.get(channel);
  if (existing) return existing;

  const created: WsConnection = {
    socket: null,
    listeners: new Set(),
    reconnectHandlers: new Set(),
    attempt: 0,
    timer: null,
    openedAt: null,
    reported: false,
    sawError: false,
  };
  connections.set(channel, created);
  return created;
};

const clearTimer = (connection: WsConnection): void => {
  if (connection.timer === null) return;
  clearTimeout(connection.timer);
  connection.timer = null;
};

const detach = (socket: WebSocket): void => {
  socket.onopen = null;
  socket.onmessage = null;
  socket.onclose = null;
  socket.onerror = null;
};

const deliver = (channel: ChannelName, connection: WsConnection, raw: string): void => {
  let payload: unknown;
  try {
    payload = JSON.parse(raw);
  } catch {
    console.warn(`[ws] ${channel}: dropped an unparseable frame`);
    return;
  }

  // Copy before iterating: a listener may unsubscribe itself inside the callback. The
  // Promise.resolve catches an async listener rejecting unobserved.
  [...connection.listeners].forEach((listener) => {
    try {
      Promise.resolve(listener(payload)).catch((error) => {
        console.error(`[ws] ${channel}: listener rejected`, error);
      });
    } catch (error) {
      console.error(`[ws] ${channel}: listener threw`, error);
    }
  });
};

// A rejected origin and an unreachable backend both surface as a bare 1006 with nothing
// logged server-side, so without the URL and origin the failure is invisible on both ends.
// Once per outage plus one escalation, so an environment with no realtime backend at all
// costs two lines per channel rather than a stream.
const reportFailure = (
  channel: ChannelName,
  connection: WsConnection,
  code: number,
  wasClean: boolean
): void => {
  const detail = [
    `url=${channelUrl(channel)}`,
    `origin=${window.location.origin}`,
    `code=${code}`,
    `wasClean=${wasClean}`,
    `socketError=${connection.sawError}`,
  ].join(' ');

  if (!connection.reported) {
    connection.reported = true;
    console.warn(`[ws] ${channel}: disconnected, retrying. ${detail}`);
  }

  if (connection.attempt + 1 === ESCALATE_AFTER_ATTEMPTS) {
    console.error(
      `[ws] ${channel}: still unreachable after ${ESCALATE_AFTER_ATTEMPTS} attempts. ${detail}`
    );
  }
};

// Equal jitter: half the ceiling plus a random half. Full jitter has a zero floor, so a
// close right after open could schedule a sub-millisecond retry and chain into a hot loop.
// The random half still decorrelates the herd, and every socket does drop at once.
const scheduleRetry = (channel: ChannelName, connection: WsConnection): void => {
  const ceiling = Math.min(MAX_DELAY_MS, BASE_DELAY_MS * 2 ** connection.attempt);
  const delay = ceiling / 2 + Math.random() * (ceiling / 2);
  connection.attempt += 1;
  connection.timer = setTimeout(() => {
    connection.timer = null;
    open(channel, connection);
  }, delay);
};

const handleClose = (
  channel: ChannelName,
  connection: WsConnection,
  code: number,
  wasClean: boolean
): void => {
  const livedLongEnough =
    connection.openedAt !== null && Date.now() - connection.openedAt >= STABLE_CONNECTION_MS;
  connection.openedAt = null;
  connection.socket = null;

  // Also reaches an already-accepted socket, once the session expires. Retrying with the
  // same credentials is a reconnect storm, so the only gate is resetTerminal().
  if (code === WS_CLOSE_UNAUTHORIZED) {
    terminal.add(channel);
    clearTimer(connection);
    console.warn(`[ws] ${channel}: 4401, session is no longer authenticated. Not retrying.`);
    notifyAuthFailure();
    return;
  }

  if (code === WS_CLOSE_UNKNOWN_ROUTE) {
    terminal.add(channel);
    clearTimer(connection);
    console.warn(`[ws] ${channel}: no such channel at ${channelUrl(channel)}. Not retrying.`);
    return;
  }

  if (connection.listeners.size === 0) return;

  if (livedLongEnough) {
    connection.attempt = 0;
    connection.reported = false;
  }

  reportFailure(channel, connection, code, wasClean);
  scheduleRetry(channel, connection);
};

const open = (channel: ChannelName, connection: WsConnection): void => {
  if (connection.socket || terminal.has(channel)) return;

  // A caller may re-open while a retry is still armed (resetTerminal does), and the next
  // scheduleRetry would overwrite the handle before anyone could cancel it.
  clearTimer(connection);
  connection.sawError = false;

  let socket: WebSocket;
  try {
    socket = new WebSocket(channelUrl(channel));
  } catch (error) {
    // A malformed base URL throws synchronously and would escape out of a useEffect.
    // Retrying cannot fix it.
    terminal.add(channel);
    console.error(`[ws] ${channel}: could not open ${channelUrl(channel)}`, error);
    return;
  }

  connection.socket = socket;

  socket.onopen = () => {
    connection.openedAt = Date.now();
    // Only after a failed attempt, so a first connect does not refetch in every consumer.
    if (connection.attempt > 0) {
      [...connection.reconnectHandlers].forEach((handler) => {
        try {
          handler();
        } catch (error) {
          console.error(`[ws] ${channel}: reconnect handler threw`, error);
        }
      });
    }
  };
  socket.onmessage = (event: MessageEvent) => {
    deliver(channel, connection, event.data as string);
  };
  socket.onerror = () => {
    // Always followed by onclose, which reports. Recorded only so that report can tell a
    // transport error from a clean server-side close.
    connection.sawError = true;
  };
  socket.onclose = (event: CloseEvent) => {
    // An abandoned socket must not drive the channel that replaced it: its 4401 would
    // poison `terminal` for a live connection.
    if (connection.socket !== socket) return;
    handleClose(channel, connection, event.code, event.wasClean);
  };
};

const teardown = (channel: ChannelName, connection: WsConnection): void => {
  clearTimer(connection);

  const socket = connection.socket;
  if (socket) {
    connection.socket = null;
    detach(socket);
    socket.close(1000);
  }

  if (connections.get(channel) === connection) connections.delete(channel);
};

export const subscribe = (
  channel: ChannelName,
  onMessage: WsListener,
  onReconnect?: WsReconnectHandler
): (() => void) => {
  const connection = ensure(channel);
  connection.listeners.add(onMessage);
  if (onReconnect) connection.reconnectHandlers.add(onReconnect);
  if (!connection.socket && connection.timer === null) open(channel, connection);

  return () => {
    connection.listeners.delete(onMessage);
    if (onReconnect) connection.reconnectHandlers.delete(onReconnect);
    if (connection.listeners.size === 0) teardown(channel, connection);
  };
};

export const closeAll = (): void => {
  [...connections.entries()].forEach(([channel, connection]) => teardown(channel, connection));
};

export const resetTerminal = (): void => {
  terminal.clear();
  authFailureNotified = false;
  authFailurePending = false;
  connections.forEach((connection, channel) => {
    // Dial immediately rather than inherit the ceiling a long outage grew.
    connection.attempt = 0;
    connection.reported = false;
    if (connection.listeners.size > 0 && !connection.socket) open(channel, connection);
  });
};

// Inert today: no channel accepts client frames yet, so everything sent here is swallowed.
// Deliberately does not queue — there is no ack path and no ordering worth preserving.
export const send = (channel: ChannelName, payload: object): void => {
  const socket = connections.get(channel)?.socket;
  if (!socket || socket.readyState !== WebSocket.OPEN) {
    console.debug(`[ws] ${channel}: send dropped, socket not open`);
    return;
  }
  socket.send(JSON.stringify(payload));
};
